import React, { useState } from 'react';
import { 
  Upload, Download, RefreshCw, Settings, 
  Database, FileText, AlertTriangle, Clock,
  Check, X, Play, Pause, ExternalLink 
} from 'lucide-react';

const SystemIntegration = () => {
  const [selectedTab, setSelectedTab] = useState('import-export');
  const [importMode, setImportMode] = useState('file');
  const [exportFormat, setExportFormat] = useState('json');

  // Sample integration data
  const recentImports = [
    {
      id: 'IMP001',
      type: 'risk-data',
      source: 'Excel Upload',
      date: '2024-03-20 14:30',
      status: 'completed',
      records: 156,
      errors: 0,
      user: 'John Smith'
    },
    {
      id: 'IMP002',
      type: 'user-data',
      source: 'HR System API',
      date: '2024-03-19 10:15',
      status: 'completed',
      records: 24,
      errors: 2,
      user: 'System'
    }
  ];

  const recentExports = [
    {
      id: 'EXP001',
      type: 'risk-report',
      format: 'Excel',
      date: '2024-03-20 15:45',
      status: 'completed',
      records: 245,
      user: 'Sarah Williams'
    }
  ];

  const apiEndpoints = [
    {
      id: 'API001',
      name: 'Risk Data API',
      endpoint: '/api/v1/risks',
      status: 'active',
      lastSync: '2024-03-20 14:00',
      nextSync: '2024-03-20 15:00',
      type: 'REST',
      method: 'GET'
    },
    {
      id: 'API002',
      name: 'User Sync API',
      endpoint: '/api/v1/users',
      status: 'active',
      lastSync: '2024-03-20 13:30',
      nextSync: '2024-03-20 14:30',
      type: 'REST',
      method: 'GET'
    }
  ];

  const StatusBadge = ({ status }) => {
    const statusStyles = {
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'active': 'bg-blue-100 text-blue-800 border-blue-200',
      'failed': 'bg-red-100 text-red-800 border-red-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const ImportExportTab = () => (
    <div className="space-y-6">
      {/* Import Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4">Data Import</h2>
        
        {/* Import Options */}
        <div className="mb-6">
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => setImportMode('file')}
              className={`px-4 py-2 rounded-md ${
                importMode === 'file' 
                  ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              File Upload
            </button>
            <button
              onClick={() => setImportMode('api')}
              className={`px-4 py-2 rounded-md ${
                importMode === 'api' 
                  ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              API Import
            </button>
          </div>

          {importMode === 'file' && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-2">
                <button className="font-medium text-blue-600 hover:text-blue-500">
                  Upload a file
                </button>
                <span className="text-gray-500"> or drag and drop</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Excel, CSV, or JSON files up to 10MB
              </p>
            </div>
          )}

          {importMode === 'api' && (
            <div className="space-y-4">
              <select className="w-full px-4 py-2 border rounded-md">
                <option value="">Select Data Source</option>
                <option value="hr">HR System</option>
                <option value="compliance">Compliance System</option>
                <option value="audit">Audit System</option>
              </select>
              <button className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Import
              </button>
            </div>
          )}
        </div>

        {/* Recent Imports */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Recent Imports</h3>
          <div className="space-y-2">
            {recentImports.map(imp => (
              <div key={imp.id} className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-md">
                <div>
                  <div className="font-medium">{imp.type}</div>
                  <div className="text-sm text-gray-500">
                    {imp.source} • {imp.records} records
                  </div>
                </div>
                <div className="text-right">
                  <StatusBadge status={imp.status} />
                  <div className="text-sm text-gray-500 mt-1">{imp.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4">Data Export</h2>
        
        {/* Export Options */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Type
            </label>
            <select className="w-full px-4 py-2 border rounded-md">
              <option value="risks">Risk Data</option>
              <option value="users">User Data</option>
              <option value="audit">Audit Log</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Export Format
            </label>
            <select 
              className="w-full px-4 py-2 border rounded-md"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
              <option value="excel">Excel</option>
            </select>
          </div>
          <button className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Export Data
          </button>
        </div>

        {/* Recent Exports */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Recent Exports</h3>
          <div className="space-y-2">
            {recentExports.map(exp => (
              <div key={exp.id} className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-md">
                <div>
                  <div className="font-medium">{exp.type}</div>
                  <div className="text-sm text-gray-500">
                    {exp.format} • {exp.records} records
                  </div>
                </div>
                <div className="text-right">
                  <StatusBadge status={exp.status} />
                  <div className="text-sm text-gray-500 mt-1">{exp.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const APIIntegrationsTab = () => (
    <div className="space-y-6">
      {/* API Status Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Active Endpoints</div>
          <div className="text-2xl font-bold">4</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Today's Syncs</div>
          <div className="text-2xl font-bold">24</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Failed Syncs</div>
          <div className="text-2xl font-bold">0</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Data Transferred</div>
          <div className="text-2xl font-bold">1.2 GB</div>
        </div>
      </div>

      {/* API Endpoints List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-medium">API Endpoints</h2>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {apiEndpoints.map(endpoint => (
              <div key={endpoint.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium">{endpoint.name}</h3>
                    <div className="text-sm text-gray-500">{endpoint.endpoint}</div>
                  </div>
                  <StatusBadge status={endpoint.status} />
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Last Sync</div>
                    <div>{endpoint.lastSync}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Next Sync</div>
                    <div>{endpoint.nextSync}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Type</div>
                    <div>{endpoint.type} ({endpoint.method})</div>
                  </div>
                </div>
                <div className="mt-4 flex justify-end space-x-3">
                  <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm rounded text-gray-700 bg-white hover:bg-gray-50">
                    <Settings className="w-4 h-4 mr-1" />
                    Configure
                  </button>
                  <button className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm rounded text-gray-700 bg-white hover:bg-gray-50">
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Sync Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">System Integration</h1>
        <div className="text-gray-600">
          Manage data imports, exports, and API integrations
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-4">
          <button
            onClick={() => setSelectedTab('import-export')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              selectedTab === 'import-export'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Import & Export
          </button>
          <button
            onClick={() => setSelectedTab('api')}
            className={`px-3 py-2 text-sm font-medium rounded-md ${
              selectedTab === 'api'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            API Integrations
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {selectedTab === 'import-export' ? <ImportExportTab /> : <APIIntegrationsTab />}
    </div>
  );
};

export default SystemIntegration;
